import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Trash2,
  Edit2,
  CalendarDays,
  Flag,
  Globe,
  Loader2,
  Sun,
  Building2 } from
'lucide-react';
import {
  getHolidayCalendars,
  createHolidayCalendar,
  addHoliday } from
'../services/calendarAPI';

const COUNTRY_OPTIONS = [
{ code: 'BR', name: 'Brazil', flag: '🇧🇷' },
{ code: 'US', name: 'United States', flag: '🇺🇸' },
{ code: 'MX', name: 'Mexico', flag: '🇲🇽' },
{ code: 'AR', name: 'Argentina', flag: '🇦🇷' },
{ code: 'CO', name: 'Colombia', flag: '🇨🇴' },
{ code: 'CL', name: 'Chile', flag: '🇨🇱' },
{ code: 'PE', name: 'Peru', flag: '🇵🇪' },
{ code: 'ES', name: 'Spain', flag: '🇪🇸' },
{ code: 'PT', name: 'Portugal', flag: '🇵🇹' },
{ code: 'DE', name: 'Germany', flag: '🇩🇪' },
{ code: 'FR', name: 'France', flag: '🇫🇷' },
{ code: 'GB', name: 'United Kingdom', flag: '🇬🇧' }];


const HOLIDAY_TYPES = [
{ value: 'public', label: 'Public Holiday', icon: Globe, color: 'bg-red-500' },
{ value: 'regional', label: 'Regional Holiday', icon: Flag, color: 'bg-orange-500' },
{ value: 'company', label: 'Company Holiday', icon: Building2, color: 'bg-blue-500' },
{ value: 'optional', label: 'Optional Holiday', icon: Sun, color: 'bg-yellow-500' }];


// Brazilian national holidays for quick setup
const BRAZILIAN_HOLIDAYS_2025 = [
{ name: 'Confraternização Universal', date: '2025-01-01', type: 'public' },
{ name: 'Carnaval', date: '2025-03-03', type: 'public' },
{ name: 'Carnaval', date: '2025-03-04', type: 'public' },
{ name: 'Sexta-feira Santa', date: '2025-04-18', type: 'public' },
{ name: 'Tiradentes', date: '2025-04-21', type: 'public' },
{ name: 'Dia do Trabalho', date: '2025-05-01', type: 'public' },
{ name: 'Corpus Christi', date: '2025-06-19', type: 'public' },
{ name: 'Independência do Brasil', date: '2025-09-07', type: 'public' },
{ name: 'Nossa Senhora Aparecida', date: '2025-10-12', type: 'public' },
{ name: 'Finados', date: '2025-11-02', type: 'public' },
{ name: 'Proclamação da República', date: '2025-11-15', type: 'public' },
{ name: 'Natal', date: '2025-12-25', type: 'public' }];


export default function HolidayCalendarPage() {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(true);
  const [calendars, setCalendars] = useState([]);
  const [showCreateCalendarDialog, setShowCreateCalendarDialog] = useState(false);
  const [showAddHolidayDialog, setShowAddHolidayDialog] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());

  // New calendar form
  const [newCalendar, setNewCalendar] = useState({
    name: '',
    year: new Date().getFullYear(),
    country_code: 'BR'
  });

  // New holiday form
  const [newHoliday, setNewHoliday] = useState({
    name: '',
    date: '',
    is_full_day: true,
    holiday_type: 'public',
    name_translations: {}
  });

  // Load holiday calendars
  useEffect(() => {
    const loadCalendars = async () => {
      setLoading(true);
      try {
        const data = await getHolidayCalendars(yearFilter);
        setCalendars(data);
      } catch (error) {
        console.error('Failed to load holiday calendars:', error);
        toast.error('Failed to load holiday calendars');
      } finally {
        setLoading(false);
      }
    };
    loadCalendars();
  }, [yearFilter]);

  // Create new calendar
  const handleCreateCalendar = async () => {
    try {
      const result = await createHolidayCalendar(newCalendar);
      toast.success('Holiday calendar created');
      setShowCreateCalendarDialog(false);
      setNewCalendar({ name: '', year: new Date().getFullYear(), country_code: 'BR' });

      // Refresh calendars
      const data = await getHolidayCalendars(yearFilter);
      setCalendars(data);

      return result;
    } catch (error) {
      console.error('Failed to create calendar:', error);
      toast.error('Failed to create holiday calendar');
      return null;
    }
  };

  // Create calendar with preset holidays
  const handleCreateWithPreset = async () => {
    const cal = await handleCreateCalendar();
    if (cal) {
      // Add Brazilian holidays
      for (const holiday of BRAZILIAN_HOLIDAYS_2025) {
        try {
          await addHoliday(cal.id, {
            name: holiday.name,
            date: new Date(holiday.date).toISOString(),
            is_full_day: true,
            holiday_type: holiday.type
          });
        } catch (e) {
          console.error('Failed to add holiday:', e);
        }
      }
      toast.success('Brazilian holidays added');

      // Refresh calendars
      const data = await getHolidayCalendars(yearFilter);
      setCalendars(data);
    }
  };

  // Add holiday to calendar
  const handleAddHoliday = async () => {
    if (!selectedCalendar) return;

    try {
      await addHoliday(selectedCalendar.id, {
        ...newHoliday,
        date: new Date(newHoliday.date).toISOString()
      });
      toast.success('Holiday added');
      setShowAddHolidayDialog(false);
      setNewHoliday({
        name: '',
        date: '',
        is_full_day: true,
        holiday_type: 'public',
        name_translations: {}
      });

      // Refresh calendars
      const data = await getHolidayCalendars(yearFilter);
      setCalendars(data);
    } catch (error) {
      console.error('Failed to add holiday:', error);
      toast.error('Failed to add holiday');
    }
  };

  // Open add holiday dialog
  const openAddHolidayDialog = (calendar) => {
    setSelectedCalendar(calendar);
    setShowAddHolidayDialog(true);
  };

  // Get country info
  const getCountryInfo = (code) => {
    return COUNTRY_OPTIONS.find((c) => c.code === code) || { code, name: code, flag: '🏳️' };
  };

  // Get holiday type info
  const getHolidayType = (type) => {
    return HOLIDAY_TYPES.find((t) => t.value === type) || HOLIDAY_TYPES[0];
  };

  return (
    <div className="p-6 space-y-6" data-testid="holiday-calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <CalendarDays className="w-7 h-7 text-red-600" />{t("holidayCalendars", "Holiday Calendars")}

          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{t("manageHolidaysFor", "Manage holidays for different countries and company-specific dates")}

          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={yearFilter.toString()} onValueChange={(v) => setYearFilter(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="year-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((year) =>
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              )}
            </SelectContent>
          </Select>
          
          <Button onClick={() => setShowCreateCalendarDialog(true)} data-testid="create-calendar-btn">
            <Plus className="w-4 h-4 mr-1" />{t("newCalendar", "New Calendar")}

          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("totalCalendars", "Total Calendars")}</p>
                <p className="text-2xl font-bold">{calendars.length}</p>
              </div>
              <CalendarDays className="w-8 h-8 text-slate-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("totalHolidays", "Total Holidays")}</p>
                <p className="text-2xl font-bold">
                  {calendars.reduce((sum, cal) => sum + (cal.holidays?.length || 0), 0)}
                </p>
              </div>
              <Flag className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("countries", "Countries")}</p>
                <p className="text-2xl font-bold">
                  {new Set(calendars.map((c) => c.country_code)).size}
                </p>
              </div>
              <Globe className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">{t("year", "Year")}</p>
                <p className="text-2xl font-bold">{yearFilter}</p>
              </div>
              <Calendar className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar List */}
      {loading ?
      <Card>
          <CardContent className="py-12">
            <div className="flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-red-600" />
            </div>
          </CardContent>
        </Card> :
      calendars.length === 0 ?
      <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <CalendarDays className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-2">{t("noHolidayCalendars", "No Holiday Calendars")}

            </h3>
              <p className="text-slate-500 mb-4">{t("createAHoliday", "Create a holiday calendar to track public and company holidays")}

            </p>
              <Button onClick={() => setShowCreateCalendarDialog(true)}>
                <Plus className="w-4 h-4 mr-1" />{t("createCalendar", "Create Calendar")}

            </Button>
            </div>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {calendars.map((calendar) => {
          const country = getCountryInfo(calendar.country_code);

          return (
            <Card key={calendar.id} data-testid={`holiday-calendar-${calendar.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{country.flag}</span>
                      <div>
                        <CardTitle className="text-lg">{calendar.name}</CardTitle>
                        <CardDescription>
                          {country.name} • {calendar.year}
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline">{calendar.holidays?.length || 0}{t("holidays", "holidays")}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Holidays List */}
                  <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                    {!calendar.holidays || calendar.holidays.length === 0 ?
                  <p className="text-sm text-slate-500 text-center py-4">{t("noHolidaysAdded", "No holidays added yet")}</p> :

                  calendar.holidays.map((holiday, index) => {
                    const typeInfo = getHolidayType(holiday.holiday_type);
                    const TypeIcon = typeInfo.icon;

                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg ${typeInfo.color} flex items-center justify-center`}>
                                <TypeIcon className="w-4 h-4 text-white" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{holiday.name}</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(holiday.date).toLocaleDateString([], {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                              })}
                                </p>
                              </div>
                            </div>
                            <Badge variant="outline" className="text-xs capitalize">
                              {holiday.holiday_type}
                            </Badge>
                          </div>);

                  })
                  }
                  </div>
                  
                  <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openAddHolidayDialog(calendar)}
                  data-testid={`add-holiday-${calendar.id}`}>
                  
                    <Plus className="w-4 h-4 mr-1" />{t("addHoliday", "Add Holiday")}

                </Button>
                </CardContent>
              </Card>);

        })}
        </div>
      }

      {/* Create Calendar Dialog */}
      <Dialog open={showCreateCalendarDialog} onOpenChange={setShowCreateCalendarDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("createHolidayCalendar", "Create Holiday Calendar")}</DialogTitle>
            <DialogDescription>{t("createANew", "Create a new holiday calendar for a specific country and year")}

            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("calendarName", "Calendar Name *")}</Label>
              <Input
                value={newCalendar.name}
                onChange={(e) => setNewCalendar((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Brazil Holidays 2025"
                className="mt-1"
                data-testid="new-calendar-name" />
              
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("country", "Country")}</Label>
                <Select
                  value={newCalendar.country_code}
                  onValueChange={(v) => setNewCalendar((prev) => ({ ...prev, country_code: v }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-calendar-country">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((country) =>
                    <SelectItem key={country.code} value={country.code}>
                        <span className="mr-2">{country.flag}</span>
                        {country.name}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{t("year", "Year")}</Label>
                <Select
                  value={newCalendar.year.toString()}
                  onValueChange={(v) => setNewCalendar((prev) => ({ ...prev, year: parseInt(v) }))}>
                  
                  <SelectTrigger className="mt-1" data-testid="new-calendar-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((year) =>
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {newCalendar.country_code === 'BR' && newCalendar.year === 2025 &&
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <p className="text-sm text-green-700 dark:text-green-400">
                  <strong>{t("tip", "Tip:")}</strong>{t("youCanAutopopulate", "You can auto-populate with Brazilian national holidays for 2025")}
              </p>
              </div>
            }
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowCreateCalendarDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            {newCalendar.country_code === 'BR' && newCalendar.year === 2025 &&
            <Button
              variant="secondary"
              onClick={handleCreateWithPreset}
              disabled={!newCalendar.name}
              data-testid="create-with-preset">{t("createWithBrazilian", "Create with Brazilian Holidays")}


            </Button>
            }
            <Button
              onClick={handleCreateCalendar}
              disabled={!newCalendar.name}
              data-testid="create-calendar-submit">{t("createEmptyCalendar", "Create Empty Calendar")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Holiday Dialog */}
      <Dialog open={showAddHolidayDialog} onOpenChange={setShowAddHolidayDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("addHoliday", "Add Holiday")}</DialogTitle>
            <DialogDescription>{t("addANew", "Add a new holiday to")}
              {selectedCalendar?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>{t("holidayName", "Holiday Name *")}</Label>
              <Input
                value={newHoliday.name}
                onChange={(e) => setNewHoliday((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Christmas Day"
                className="mt-1"
                data-testid="new-holiday-name" />
              
            </div>
            
            <div>
              <Label>{t("date", "Date *")}</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday((prev) => ({ ...prev, date: e.target.value }))}
                className="mt-1"
                data-testid="new-holiday-date" />
              
            </div>
            
            <div>
              <Label>{t("holidayType", "Holiday Type")}</Label>
              <Select
                value={newHoliday.holiday_type}
                onValueChange={(v) => setNewHoliday((prev) => ({ ...prev, holiday_type: v }))}>
                
                <SelectTrigger className="mt-1" data-testid="new-holiday-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOLIDAY_TYPES.map((type) =>
                  <SelectItem key={type.value} value={type.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded ${type.color}`}></div>
                        {type.label}
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newHoliday.is_full_day}
                onCheckedChange={(checked) => setNewHoliday((prev) => ({ ...prev, is_full_day: checked }))}
                id="full-day" />
              
              <Label htmlFor="full-day" className="font-normal cursor-pointer">{t("fullDayHoliday", "Full day holiday")}</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddHolidayDialog(false)}>{t("cancel", "Cancel")}

            </Button>
            <Button
              onClick={handleAddHoliday}
              disabled={!newHoliday.name || !newHoliday.date}
              data-testid="add-holiday-submit">{t("addHoliday", "Add Holiday")}


            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

}